<template>
  <TopLevelPageShell
    title="Archived Inventory"
    subtitle="Review vehicles removed from current inventory."
  >
    <template #actions>
      <v-btn
        variant="outlined"
        color="primary"
        prepend-icon="mdi-car-multiple"
        @click="navigateToCurrentInventory"
      >
        Current Inventory
      </v-btn>
    </template>

        <v-sheet class="control-bar pa-3 mb-4" rounded>
          <InventorySearchToolbarRow>
            <v-text-field
              v-model="searchQuery"
              placeholder="Search VIN, make, model, stock #, trim, or year"
              density="comfortable"
              variant="outlined"
              hide-details
              prepend-inner-icon="mdi-magnify"
              class="control-input"
              @input="handleSearchInput"
              @keyup.enter="triggerSearchNow"
            >
              <template #append-inner>
                <v-btn
                  color="primary"
                  size="small"
                  variant="tonal"
                  class="ml-2"
                  :loading="isLoading"
                  @click="triggerSearchNow"
                >
                  Search
                </v-btn>
              </template>
            </v-text-field>
          </InventorySearchToolbarRow>

          <v-row v-if="hasActiveSearch" no-gutters class="align-center mt-2">
            <v-col cols="12" class="d-flex justify-end">
              <v-btn
                variant="text"
                color="primary"
                :disabled="isLoading"
                @click="clearSearch"
              >
                Reset Filters
              </v-btn>
            </v-col>
          </v-row>
        </v-sheet>

        <div class="text-caption text-grey-darken-1 mb-3" v-if="!isLoading && totalItems > 0">
          Showing {{ tableItems.length }} of {{ totalItems }} archived vehicles
        </div>

        <v-data-table-server
          :headers="columnOptions"
          :items="tableItems"
          :items-length="totalItems"
          :loading="isLoading"
          :items-per-page="tableOptions.itemsPerPage"
          class="elevation-1 archived-inventory-table"
          @update:options="updateOptions"
          @click:row="handleRowClick"
        >
          <template #item.vin="{ item }">
            <span class="font-weight-medium">{{ item.vin }}</span>
          </template>

          <template #item.stock_number="{ item }">
            <v-tooltip v-if="item.stock_number && item.stock_number.length > 7" location="top" :text="item.stock_number">
              <template #activator="{ props }">
                <span v-bind="props" style="white-space: nowrap">{{ item.stock_number.slice(0, 7) }}...</span>
              </template>
            </v-tooltip>
            <span v-else>{{ item.stock_number ?? '-' }}</span>
          </template>

          <template #item.model="{ item }">
            <v-tooltip v-if="item.trim" location="top">
              <template #activator="{ props }">
                <span v-bind="props">{{ item.model ?? '-' }}</span>
              </template>
              <span>Trim: {{ item.trim }}</span>
            </v-tooltip>
            <span v-else>{{ item.model ?? '-' }}</span>
          </template>

          <template #item.source="{ item }">
            <v-chip
              size="x-small"
              variant="tonal"
              :color="item.source === 'manual' ? 'deep-purple' : 'primary'"
            >
              {{ item.source === 'manual' ? 'User' : 'Feed' }}
            </v-chip>
          </template>

          <template #item.archiveReason="{ item }">
            <span>{{ item.archiveReason ? formatVehicleHistoryReasonLabel(item.archiveReason) : '-' }}</span>
          </template>

          <template #item.archivedAt="{ item }">
            <DateTimeWithDealerTooltip :value="item.archivedAt" :text="formatISODate(item.archivedAt)" />
          </template>

          <template #no-data>
            <div class="text-center pa-8">
              <v-icon icon="mdi-archive-outline" size="80" color="grey" class="mb-4" />
              <div class="text-h6 mb-2">No archived vehicles</div>
              <div class="text-body-2 text-grey mb-4">
                Archived vehicles will appear here after they are removed from current inventory.
              </div>
              <v-btn variant="outlined" color="primary" prepend-icon="mdi-car-multiple" @click="navigateToCurrentInventory">
                Current Inventory
              </v-btn>
            </div>
          </template>
        </v-data-table-server>
  </TopLevelPageShell>
</template>

<script lang="ts" setup>
import { computed, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { INVENTORY_STATE } from '@jazel-auto/ise-contracts/dist/inventory.js'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import {
  fetchInventory,
  type InventoryVehicle,
} from '@/services/inventory'
import { formatVehicleHistoryReasonLabel } from '@/utils/vehicleStatusHistoryFormatting'
import DateTimeWithDealerTooltip from '@/components/DateTimeWithDealerTooltip.vue'
import InventorySearchToolbarRow from '@/components/InventorySearchToolbarRow.vue'
import TopLevelPageShell from '@/components/layout/TopLevelPageShell.vue'

const router = useRouter()
const route = useRoute()
const accountSettings = useAccountSettingsStore()
const endpoint = import.meta.env.VITE_ISE_BACKEND_URL

const isLoading = ref(false)
const searchQuery = ref('')
const isApplyingRouteFilters = ref(false)
const hasAppliedInitialRouteFilters = ref(false)
const activeRequestId = ref(0)
const rawItems = ref<InventoryVehicle[]>([])
const totalItems = ref(0)

const tableOptions = reactive({
  page: 1,
  itemsPerPage: 50,
  sortBy: [] as unknown[],
})

const columnOptions = [
  { title: 'VIN', key: 'vin', sortable: false },
  { title: 'Stock #', key: 'stock_number', sortable: false, width: '90px' },
  { title: 'Year', key: 'year', sortable: false },
  { title: 'Make', key: 'make', sortable: false },
  { title: 'Model', key: 'model', sortable: false },
  { title: 'Source', key: 'source', sortable: false },
  { title: 'Archive Reason', key: 'archiveReason', sortable: false },
  { title: 'Date Archived', key: 'archivedAt', sortable: false },
]

const tableItems = computed(() => rawItems.value)

const hasActiveSearch = computed(() => searchQuery.value.trim().length > 0)

const applyRouteFilters = () => {
  const q = route.query || {}
  const search = typeof q.q === 'string' ? q.q : undefined

  isApplyingRouteFilters.value = true
  try {
    if (search != null) searchQuery.value = search
  } finally {
    isApplyingRouteFilters.value = false
    hasAppliedInitialRouteFilters.value = true
  }
}

const fetchData = async () => {
  if (!accountSettings.accountId) return

  const requestId = ++activeRequestId.value
  isLoading.value = true
  try {
    const offset = (tableOptions.page - 1) * tableOptions.itemsPerPage
    const limit = tableOptions.itemsPerPage
    const q = searchQuery.value.trim().length > 0 ? searchQuery.value.trim() : undefined

    const response = await fetchInventory(endpoint, {
      jazelId: accountSettings.accountId,
      limit,
      offset,
      inventoryState: INVENTORY_STATE.ARCHIVED,
      q,
    })

    if (requestId !== activeRequestId.value) return

    rawItems.value = response.vehicles || []
    totalItems.value = response.pagination?.total ?? rawItems.value.length
  } catch (error) {
    console.error('Failed to load archived inventory', error)
    rawItems.value = []
    totalItems.value = 0
  } finally {
    if (requestId === activeRequestId.value) {
      isLoading.value = false
    }
  }
}

const updateOptions = (options: any) => {
  const changedPage = tableOptions.page !== options.page
  const changedItemsPerPage = tableOptions.itemsPerPage !== options.itemsPerPage

  tableOptions.page = options.page
  tableOptions.itemsPerPage = options.itemsPerPage

  if (changedPage || changedItemsPerPage) {
    fetchData()
  }
}

const handleSearchInput = () => {
  tableOptions.page = 1
}

const triggerSearchNow = () => {
  tableOptions.page = 1
  fetchData()
}

const clearSearch = () => {
  searchQuery.value = ''
  tableOptions.page = 1
  fetchData()
}

watch(
  () => route.query,
  () => {
    applyRouteFilters()
    if (accountSettings.accountId) {
      tableOptions.page = 1
      fetchData()
    }
  },
  { immediate: true },
)

watch(
  () => accountSettings.accountId,
  (id) => {
    if (id) {
      if (!hasAppliedInitialRouteFilters.value) {
        applyRouteFilters()
      }
      tableOptions.page = 1
      fetchData()
    }
  },
  { immediate: true },
)

const navigateToCurrentInventory = () => {
  router.push('/inventory')
}

const navigateToDetails = (vehicle: InventoryVehicle) => {
  router.push({
    name: 'VehicleDetails',
    params: { vin: vehicle.vin },
    query: { from: 'archived' },
  })
}

const handleRowClick = (_event: any, context: any) => {
  const vehicle: InventoryVehicle | undefined = context?.item?.raw ?? context?.item
  if (!vehicle) return
  navigateToDetails(vehicle)
}

const formatISODate = (isoDate: string | undefined): string => {
  if (!isoDate) return '-'
  try {
    const date = new Date(isoDate)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return '-'
  }
}

</script>

<style scoped>
.control-bar {
  background-color: #f8fafc;
  color: #111827;
  border: 1px solid #e5e7eb;
}

.archived-inventory-table :deep(.v-data-table thead th),
.archived-inventory-table :deep(.v-table thead th),
.archived-inventory-table :deep(.v-data-table__th),
.archived-inventory-table :deep(.v-data-table__th--sticky) {
  background-color: #f5f5f5 !important;
}

.archived-inventory-table :deep(.v-data-table__th .v-data-table-header__content > span),
.archived-inventory-table :deep(.v-data-table__th--sticky .v-data-table-header__content > span) {
  font-weight: 700 !important;
}

.archived-inventory-table :deep(tbody tr) {
  cursor: pointer;
}
</style>
